import { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Avatar,
  Grid,
  Skeleton,
} from "@mui/material";
import {
  AttachMoney as DollarIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import NatureIcon from '@mui/icons-material/Nature';
import ForestIcon from '@mui/icons-material/Forest';
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const SoilHealthCard = ({ siteId }) => {
  const { accessToken } = useAuth();
  const [soilData, setSoilData] = useState({
    moisture: { value: null, change: null, changeValue: null },
    pHChange: null,
    temperature: { value: null, change: null, changeValue: null },
    loading: true,
    error: null,
  });
  const navigate = useNavigate();

  const baseURL = process.env.REACT_APP_BASEURL;

  // Handle "More" button click
  const handleMoreClick = () => {
    navigate("/dashboard/statistics", {
      state: { siteId: siteId !== "all" ? siteId : null },
    });
  };

  useEffect(() => {
    const fetchSoilData = async () => {
      setSoilData((prev) => ({ ...prev, loading: true, error: null }));
      try {
        // Fetch devices based on siteId
        const deviceUrl =
          siteId === "all"
            ? `${baseURL}/devices`
            : `${baseURL}/devices/?site=${siteId}`;
        const devicesRes = await axios.get(deviceUrl, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const deviceIds = devicesRes.data.map((d) => d.id).join(",");

        if (!deviceIds) {
          setSoilData({
            moisture: { value: "N/A", change: "N/A", changeValue: null },
            pHChange: "N/A",
            temperature: { value: "N/A", change: "N/A", changeValue: null },
            loading: false,
            error: null,
          });
          return;
        }

        // Fetch aggregated measurements
        const endDate = new Date().toISOString();
        const startDate = new Date(
          new Date(endDate).setDate(new Date(endDate).getDate() - 7)
        ).toISOString();
        const aggRes = await axios.get(`${baseURL}/measurements/aggregated`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: {
            device: deviceIds,
            since: startDate,
            until: endDate,
            window_size: 1,
            aggregate_all: true,
            fields: "moisture,temperature,water_balance",
          },
        });
        const data = aggRes.data[0] || {};

        // Calculate soil data
        const moistureMean = data.moisture?.mean || null;
        const moistureMin = data.moisture?.min || null;
        const moistureChange = moistureMean
          ? (((moistureMean - moistureMin)) * 100).toFixed(1)
          : null;
        const temperatureMean = data.temperature?.mean || null;
        const temperatureMin = data.temperature?.min || null;
        const temperatureChange = temperatureMean
          ? (temperatureMean - temperatureMin).toFixed(1)
          : null;
        const pHChange = moistureChange ? Math.min(moistureChange, 8) : null;

        setSoilData({
          moisture: {
            value: moistureMean ? `${(moistureMean).toFixed(3)}` : "N/A",
            change: moistureChange ? `${moistureChange}%` : "N/A",
            changeValue: moistureChange,
            minValue: moistureMin ? `${(moistureMin)}` : 'N/A'
          },
          pHChange: pHChange ? `+${pHChange}%` : "N/A",
          temperature: {
            value: temperatureMean ? `${temperatureMean.toFixed(1)}°C` : "N/A",
            change: temperatureChange ? `${temperatureChange}°C` : "N/A",
            changeValue: temperatureChange,
            minValue: temperatureMin ? `${temperatureMin.toFixed(1)}°C` : "N/A",
          },
          loading: false,
        });
      } catch (error) {
        console.error(
          "Soil health data fetch error:",
          error.response ? error.response.data : error.message
        );
        setSoilData((prev) => ({
          ...prev,
          loading: false,
          error: "Failed to load soil data",
        }));
      }
    };
    fetchSoilData();
  }, [accessToken, siteId, baseURL]);

  const { moisture, pHChange, temperature, loading, error } = soilData;

  if (loading)
    return (
      <Card
        sx={{
          width: "100%",
          height: "auto",
          bgcolor: "#F59E0B",
          borderRadius: 15,
          border: "2px solid black",
          color: "black",
          boxShadow: 3,
        }}
      >
        <CardContent>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <Skeleton
              variant="circular"
              width={40}
              height={40}
              sx={{ bgcolor: "rgba(255, 255, 255, 0.9)" }}
            />
            <Skeleton
              variant="text"
              width={200}
              height={40}
              sx={{ bgcolor: "rgba(0, 0, 0, 0.1)" }}
            />
          </Box>

          {/* Soil Moisture Section */}
          <Box sx={{ mb: 2 }}>
            <Skeleton
              variant="text"
              width={100}
              height={20}
              sx={{ mb: 0.5, bgcolor: "rgba(0, 0, 0, 0.1)" }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Skeleton
                variant="text"
                width={100}
                height={48}
                sx={{ bgcolor: "rgba(0, 0, 0, 0.1)" }}
              />
              <Skeleton
                variant="rectangular"
                width={80}
                height={32}
                sx={{ borderRadius: 10, bgcolor: "rgba(0, 0, 0, 0.9)" }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 1,
              }}
            >
              <Skeleton
                variant="text"
                width={120}
                height={20}
                sx={{ bgcolor: "rgba(0, 0, 0, 0.1)" }}
              />
            </Box>
          </Box>

          <Skeleton
            variant="rectangular"
            width="100%"
            height={1}
            sx={{ my: 2, bgcolor: "rgba(0, 0, 0, 0.2)" }}
          />

          {/* Soil Temperature Section */}
          <Box>
            <Skeleton
              variant="text"
              width={120}
              height={20}
              sx={{ mb: 0.5, bgcolor: "rgba(0, 0, 0, 0.1)" }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Skeleton
                variant="text"
                width={100}
                height={48}
                sx={{ bgcolor: "rgba(0, 0, 0, 0.1)" }}
              />
              <Skeleton
                variant="rectangular"
                width={80}
                height={32}
                sx={{ borderRadius: 10, bgcolor: "rgba(0, 0, 0, 0.9)" }}
              />
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              <Skeleton
                variant="text"
                width={100}
                height={20}
                sx={{ bgcolor: "rgba(0, 0, 0, 0.1)" }}
              />
              <Skeleton
                variant="text"
                width={80}
                height={20}
                sx={{ bgcolor: "rgba(0, 0, 0, 0.1)" }}
              />
              <Skeleton
                variant="text"
                width={80}
                height={20}
                sx={{ bgcolor: "rgba(0, 0, 0, 0.1)" }}
              />
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              <Skeleton
                variant="text"
                width={100}
                height={20}
                sx={{ bgcolor: "rgba(0, 0, 0, 0.1)" }}
              />
              <Skeleton
                variant="text"
                width={80}
                height={20}
                sx={{ bgcolor: "rgba(0, 0, 0, 0.1)" }}
              />
              <Skeleton
                variant="text"
                width={80}
                height={20}
                sx={{ bgcolor: "rgba(0, 0, 0, 0.1)" }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                mt: 1,
              }}
            >
              <Skeleton
                variant="text"
                width={60}
                height={20}
                sx={{ bgcolor: "rgba(0, 0, 0, 0.1)" }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
    );

  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Card
      sx={{
        width: "100%",
        height: "auto",
        bgcolor: "#F59E0B",
        borderRadius: 15,
        border: "2px solid black",
        color: "black",
        boxShadow: 3,
      }}
    >
      <CardContent>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <Avatar sx={{ bgcolor: "#fbd89c", color: "black" }}>
            <ForestIcon />
          </Avatar>
          <Typography variant="h4" component="h2" fontWeight="bold">
            Soil Health Improvement
          </Typography>
        </Box>

        {/* Soil Moisture Section */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Soil Moisture:
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h3" component="div" fontWeight="bold">
              {moisture.value}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                bgcolor: "black",
                borderRadius: 10,
                border: "2px solid white",
                px: 1.5,
                py: 0.5,
              }}
            >
              {moisture.changeValue !== null &&
                (moisture.changeValue > 0 ? (
                  <ArrowUpIcon sx={{ fontSize: 16, mr: 0.5, color: "green" }} />
                ) : moisture.changeValue < 0 ? (
                  <ArrowDownIcon sx={{ fontSize: 16, mr: 0.5, color: "red" }} />
                ) : null)}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color:
                    moisture.changeValue !== null
                      ? moisture.changeValue > 0
                        ? "green"
                        : moisture.changeValue < 0
                        ? "red"
                        : "white"
                      : "white",
                }}
              >
                {moisture.change || "N/A"}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <ArrowUpIcon fontSize="small" sx={{ color: "green", mr: 0.5 }} />
              <Typography variant="body2">{moisture.change}{` (from Min Value ${moisture.minValue})`}</Typography>
            </Box>
            <Box
              sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
              onClick={handleMoreClick}
            >
              <Typography variant="body2">More</Typography>
              <ChevronRightIcon fontSize="small" />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "rgba(0,0,0,0.2)", my: 2 }} />

        {/* Soil Temperature Section */}
        <Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Soil Temperature:
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h3" component="div" fontWeight="bold">
              {temperature.value}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                bgcolor: "black",
                borderRadius: 10,
                border: "2px solid white",
                px: 1.5,
                py: 0.5,
              }}
            >
              {temperature.changeValue !== null &&
                (temperature.changeValue > 0 ? (
                  <ArrowUpIcon sx={{ fontSize: 16, mr: 0.5, color: "red" }} />
                ) : temperature.changeValue < 0 ? (
                  <ArrowDownIcon sx={{ fontSize: 16, mr: 0.5, color: "green" }} />
                ) : null)}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color:
                    temperature.changeValue !== null
                      ? temperature.changeValue > 0
                        ? "red"
                        : temperature.changeValue < 0
                        ? "green"
                        : "white"
                      : "white",
                }}
              >
                {temperature.change || "N/A"}
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              {/* <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body2">pH Level</Typography>
                <ArrowUpIcon
                  fontSize="small"
                  sx={{ color: "green", mx: 0.5 }}
                />
                <Typography variant="body2">{pHChange}</Typography>
              </Box> */}
            </Grid>
            <Grid item xs={6}>
              {/* <Typography variant="body2" sx={{ color: "green" }}>
                (Optimal)
              </Typography> */}
            </Grid>
          </Grid>

          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {/* <Typography variant="body2">Temperature</Typography> */}
                {temperature.changeValue > 0 ? (
                  <ArrowUpIcon
                    fontSize="small"
                    sx={{ color: "red", mx: 0.5 }}
                  />
                ) : (
                  <ArrowDownIcon
                    fontSize="small"
                    sx={{ color: "green", mx: 0.5 }}
                  />
                )}
                <Typography variant="body2">{temperature.change} (from Min Value: {temperature.minValue})</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              {/* <Typography variant="body2" sx={{ color: temperature.change > 0 ? "green" : "red" }}>
                {temperature.changeValue > 0 ? "(Decreased stability)" : "(Improved stability)"}
              </Typography> */}
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mt: 1,
              cursor: "pointer",
            }}
            onClick={handleMoreClick}
          >
            <Typography variant="body2">More</Typography>
            <ChevronRightIcon fontSize="small" />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SoilHealthCard;